import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Client-side compatibility redirect for old hash-based URLs.
 * Converts hash URLs like /#/dashboard to clean paths like /marketplace.
 * This ensures old links and bookmarks still work after switching to BrowserRouter.
 */
export function HashUrlRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only process if there's a hash in the URL
    if (window.location.hash && window.location.hash.startsWith('#/')) {
      const hashPath = window.location.hash.slice(1); // Remove the '#'
      
      // Map old hash routes to new clean routes
      const routeMap: Record<string, string> = {
        '/dashboard': '/marketplace',
        '/property': '/property', // Will redirect to /projects/:slug
        '/projects': '/projects', // New slug-based route
        '/tools': '/tools',
        '/shortlist': '/shortlist',
        '/portfolio': '/portfolio',
        '/account': '/account',
        '/settings': '/settings',
        '/compare': '/compare',
        '/login': '/login',
        '/onboarding': '/onboarding',
        '/analytics': '/analytics',
        '/developer-hub': '/developer-hub',
        '/admin': '/admin',
        '/': '/',
      };

      // Check if hash path matches a known route (exact or starts with)
      let targetPath = routeMap[hashPath];
      
      // If no exact match, try to find a prefix match (e.g., /property/123)
      if (!targetPath) {
        for (const [oldPath, newPath] of Object.entries(routeMap)) {
          if (hashPath.startsWith(oldPath)) {
            // Preserve the rest of the path (e.g., /property/123 -> /property/123)
            targetPath = hashPath.replace(oldPath, newPath);
            break;
          }
        }
      }

      // If we found a mapping, redirect to the clean path
      if (targetPath) {
        // Replace the hash URL with the clean path
        window.history.replaceState(null, '', targetPath + window.location.search);
        navigate(targetPath, { replace: true });
      } else {
        // Unknown hash route - just remove the hash and let React Router handle it
        window.history.replaceState(null, '', location.pathname + location.search);
      }
    }
  }, [navigate, location]);

  return null; // This component doesn't render anything
}
